import React, { useState, useEffect, useCallback } from 'react';
import { getSwingSettings, updateSwingSettings } from '../../api/client';

const FREQ_OPTIONS = [
  { value: 'weekly',    label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly',  label: 'Monthly' },
];

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#ccc' }}>{label}</label>
      {hint && <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>{hint}</div>}
      {children}
    </div>
  );
}

function NumInput({ value, onChange, min, max, step = 0.1, unit = '' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        min={min}
        max={max}
        step={step}
        style={{ padding: '8px 12px', background: '#1e1e1e', border: '1px solid #444', color: '#fff', borderRadius: 6, width: 140 }}
      />
      {unit && <span style={{ fontSize: 12, color: '#666' }}>{unit}</span>}
      <span style={{ fontSize: 11, color: '#444' }}>{min} – {max}</span>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 40, height: 22, borderRadius: 11, background: checked ? '#4CAF50' : '#333',
          position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: checked ? 21 : 3, width: 16, height: 16,
          borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
        }} />
      </div>
      <span style={{ fontSize: 13, color: '#ccc' }}>{label}</span>
    </label>
  );
}

export default function SwingSettings() {
  const [form, setForm]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await getSwingSettings();
      const d = res.data;
      setForm({
        capital:               d.capital                ?? 500000,
        risk_pct:              d.risk_pct               ?? 1.0,
        max_alloc_pct:         d.max_alloc_pct          ?? 10,
        sector_cap_pct:        d.sector_cap_pct         ?? 25,
        n_positions:           d.n_positions            ?? 10,
        sector_cap_enabled:    d.sector_cap_enabled     ?? true,
        alert_telegram:        d.alert_telegram         ?? false,
        alert_ui:              d.alert_ui               ?? true,
        rebalance_frequency:   d.rebalance_frequency    ?? 'monthly',
      });
    } catch {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const validate = () => {
    const f = form;
    if (parseFloat(f.capital) < 10000) return 'Capital must be ≥ ₹10,000';
    if (parseFloat(f.risk_pct) < 0.1 || parseFloat(f.risk_pct) > 5) return 'Risk % must be 0.1–5';
    if (parseFloat(f.max_alloc_pct) <= parseFloat(f.risk_pct)) return 'Max Alloc % must be greater than Risk %';
    if (parseInt(f.n_positions) < 5 || parseInt(f.n_positions) > 30) return 'N Positions must be 5–30';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError('');
    try {
      await updateSwingSettings({
        ...form,
        capital:        parseFloat(form.capital),
        risk_pct:       parseFloat(form.risk_pct),
        max_alloc_pct:  parseFloat(form.max_alloc_pct),
        sector_cap_pct: parseFloat(form.sector_cap_pct),
        n_positions:    parseInt(form.n_positions),
      });
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24, color: '#888' }}>Loading settings...</div>;
  if (!form) return null;

  return (
    <div style={{ maxWidth: 640 }}>
      {error && <div style={{ color: '#f44', marginBottom: 16, padding: '10px 14px', background: '#2a1010', borderRadius: 6 }}>{error}</div>}
      {success && <div style={{ color: '#4CAF50', marginBottom: 16, padding: '10px 14px', background: '#0a2010', borderRadius: 6 }}>{success}</div>}

      {/* Capital & Position sizing */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: '#fff' }}>Capital & Position Sizing</div>

        <Field label="Total Capital" hint="The amount allocated to the Swing Trader portfolio.">
          <NumInput value={form.capital} onChange={v => set('capital', v)} min={10000} max={100000000} step={10000} unit="₹" />
        </Field>

        <Field label="Risk % per Trade" hint="Maximum % of capital risked per position (Entry → SL distance).">
          <NumInput value={form.risk_pct} onChange={v => set('risk_pct', v)} min={0.1} max={5} step={0.1} unit="%" />
        </Field>

        <Field label="Max Allocation % per Position" hint="Maximum % of capital in a single stock. Must be greater than Risk %.">
          <NumInput value={form.max_alloc_pct} onChange={v => set('max_alloc_pct', v)} min={5} max={25} step={0.5} unit="%" />
        </Field>

        <Field label="Target Positions" hint="Target number of concurrent positions in the portfolio.">
          <NumInput value={form.n_positions} onChange={v => set('n_positions', v)} min={5} max={30} step={1} unit="stocks" />
        </Field>
      </div>

      {/* Sector constraints */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: '#fff' }}>Sector Constraints</div>

        <Field label="" hint="">
          <Toggle
            checked={form.sector_cap_enabled}
            onChange={v => set('sector_cap_enabled', v)}
            label="Enable sector concentration cap"
          />
        </Field>

        {form.sector_cap_enabled && (
          <Field label="Max Sector Allocation %" hint="Maximum % of capital that can be in any single sector.">
            <NumInput value={form.sector_cap_pct} onChange={v => set('sector_cap_pct', v)} min={15} max={50} step={1} unit="%" />
          </Field>
        )}
      </div>

      {/* Alerts */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: '#fff' }}>Alerts</div>

        <Field label="" hint="">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Toggle
              checked={form.alert_ui}
              onChange={v => set('alert_ui', v)}
              label="Show alerts in dashboard UI"
            />
            <Toggle
              checked={form.alert_telegram}
              onChange={v => set('alert_telegram', v)}
              label="Send alerts via Telegram"
            />
          </div>
        </Field>
      </div>

      {/* Rebalance */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: '#fff' }}>Rebalance Schedule</div>

        <Field label="Frequency" hint="How often to automatically trigger a rebalance check.">
          <select
            value={form.rebalance_frequency}
            onChange={e => set('rebalance_frequency', e.target.value)}
            style={{ padding: '8px 12px', background: '#1e1e1e', border: '1px solid #444', color: '#fff', borderRadius: 6, minWidth: 220 }}
          >
            {FREQ_OPTIONS.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </Field>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          padding: '10px 28px', background: saving ? '#555' : '#4CAF50',
          color: '#fff', border: 'none', borderRadius: 6,
          cursor: saving ? 'not-allowed' : 'pointer',
          fontWeight: 700, fontSize: 14,
        }}
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}
